import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Skeleton,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import Iconify from "../../../../components/iconify";
import useAuth from "../../../../hooks/useAuth";
import {
  useAddTrainingAttendeesMutation,
  useDeleteTrainingMutation,
  useGetTrainingQuery,
  usePatchTrainingAttendeeMutation,
  useRemoveTrainingAttendeeMutation,
  useSearchTrainingMembersQuery,
  useUpdateTrainingMutation,
} from "../../../../features/trainings/trainingsApiSlice";
import {
  EMPTY_ARRAY,
  formatDegreeLabel,
  formatMemberName,
  getInitials,
  readRecentAttendeesBySite,
  toId,
  toLocalDateLabel,
  toLocalTimeLabel,
  writeRecentAttendeesBySite,
} from "./attendanceUtils";

const INITIAL_FEEDBACK = {
  open: false,
  message: "",
  severity: "success",
};

const PRESENT_LABEL = {
  true: "Anwesend",
  false: "Abwesend",
};

const canEditWhileClosed = ({ isAdmin, isSuperAdmin }) => isAdmin || isSuperAdmin;
const canReopenTraining = ({ isUser, isEditor, isAdmin, isSuperAdmin }) =>
  isUser || isEditor || isAdmin || isSuperAdmin;

export default function TrainingAttendanceDetailView() {
  const navigate = useNavigate();
  const { trainingId } = useParams();
  const searchInputRef = useRef(null);
  const { isUser, isEditor, isAdmin, isSuperAdmin } = useAuth();
  const canEditClosedTraining = canEditWhileClosed({ isAdmin, isSuperAdmin });
  const canReopenClosedTraining = canReopenTraining({
    isUser,
    isEditor,
    isAdmin,
    isSuperAdmin,
  });

  const [feedback, setFeedback] = useState(INITIAL_FEEDBACK);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [recentAttendees, setRecentAttendees] = useState(EMPTY_ARRAY);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuMemberId, setMenuMemberId] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);

  const {
    data: training,
    isFetching: trainingLoading,
    isError: trainingError,
    refetch: refetchTraining,
  } = useGetTrainingQuery(trainingId, {
    skip: !trainingId,
  });

  const [addTrainingAttendees, { isLoading: addingAttendee }] =
    useAddTrainingAttendeesMutation();
  const [patchTrainingAttendee, { isLoading: patchingAttendee }] =
    usePatchTrainingAttendeeMutation();
  const [removeTrainingAttendee, { isLoading: removingAttendee }] =
    useRemoveTrainingAttendeeMutation();
  const [updateTraining, { isLoading: updatingTraining }] =
    useUpdateTrainingMutation();
  const [deleteTraining, { isLoading: deletingTraining }] =
    useDeleteTrainingMutation();

  const isReadOnly = training?.status === "closed" && !canEditClosedTraining;

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
    }, 250);
    return () => clearTimeout(timeout);
  }, [searchTerm]);

  useEffect(() => {
    if (!training?.site?._id) {
      setRecentAttendees(EMPTY_ARRAY);
      return;
    }
    setRecentAttendees(readRecentAttendeesBySite(training.site._id));
  }, [training?.site?._id]);

  useEffect(() => {
    if (!trainingLoading && !isReadOnly && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [trainingLoading, isReadOnly]);

  const searchQueryArgs = useMemo(
    () => ({ q: debouncedSearch, limit: 10 }),
    [debouncedSearch],
  );

  const { data: searchedMembersResponse = EMPTY_ARRAY, isFetching: searching } =
    useSearchTrainingMembersQuery(searchQueryArgs, {
      skip: debouncedSearch.length < 2 || isReadOnly,
    });

  const attendees = useMemo(
    () =>
      Array.isArray(training?.attendees) ? training.attendees : EMPTY_ARRAY,
    [training?.attendees],
  );

  const attendeeIds = useMemo(
    () => new Set(attendees.map((attendee) => String(attendee?.memberId))),
    [attendees],
  );

  const sortedAttendees = useMemo(() => {
    return [...attendees].sort((left, right) => {
      const leftTime = new Date(left?.addedAt || 0).getTime();
      const rightTime = new Date(right?.addedAt || 0).getTime();
      return rightTime - leftTime;
    });
  }, [attendees]);

  const searchedMembers = useMemo(() => {
    const source = Array.isArray(searchedMembersResponse)
      ? searchedMembersResponse
      : EMPTY_ARRAY;
    return source.filter((member) => !attendeeIds.has(toId(member)));
  }, [attendeeIds, searchedMembersResponse]);

  const recentCandidates = useMemo(
    () =>
      recentAttendees.filter((member) => !attendeeIds.has(toId(member))).slice(0, 10),
    [attendeeIds, recentAttendees],
  );

  const presentCount =
    typeof training?.presentCount === "number"
      ? training.presentCount
      : attendees.filter((attendee) => attendee?.present).length;
  const attendeeCount =
    typeof training?.attendeeCount === "number"
      ? training.attendeeCount
      : attendees.length;

  const handleAddMember = async (member) => {
    if (isReadOnly || !trainingId) return;

    const memberId = toId(member);
    if (!memberId || attendeeIds.has(memberId)) return;

    try {
      await addTrainingAttendees({
        id: trainingId,
        memberIds: [memberId],
        memberSnapshots: [member],
      }).unwrap();

      if (training?.site?._id) {
        const nextRecent = writeRecentAttendeesBySite(training.site._id, [member]);
        setRecentAttendees(nextRecent);
      }

      setSearchTerm("");
      setFeedback({
        open: true,
        message: `${formatMemberName(member)} hinzugefügt.`,
        severity: "success",
      });
    } catch (error) {
      setFeedback({
        open: true,
        message: error?.data?.message || "Mitglied konnte nicht hinzugefügt werden.",
        severity: "error",
      });
    }
  };

  const handleTogglePresent = async (attendee) => {
    if (isReadOnly || !trainingId) return;

    try {
      await patchTrainingAttendee({
        id: trainingId,
        memberId: attendee?.memberId,
        present: !attendee?.present,
      }).unwrap();

      setFeedback({
        open: true,
        message: "Anwesenheit aktualisiert.",
        severity: "success",
      });
    } catch (error) {
      setFeedback({
        open: true,
        message: error?.data?.message || "Aktualisierung der Anwesenheit fehlgeschlagen.",
        severity: "error",
      });
    }
  };

  const handleRemoveAttendee = async (memberId) => {
    if (isReadOnly || !trainingId) return;

    setMenuAnchor(null);
    setMenuMemberId("");

    try {
      await removeTrainingAttendee({ id: trainingId, memberId }).unwrap();
      setFeedback({
        open: true,
        message: "Teilnehmer entfernt.",
        severity: "success",
      });
    } catch (error) {
      setFeedback({
        open: true,
        message: error?.data?.message || "Teilnehmer konnte nicht entfernt werden.",
        severity: "error",
      });
    }
  };

  const handleCloseTraining = async () => {
    if (!trainingId || !training) return;

    const nextStatus = training?.status === "closed" ? "open" : "closed";

    try {
      await updateTraining({ id: trainingId, status: nextStatus }).unwrap();
      setFeedback({
        open: true,
        message:
          nextStatus === "closed"
            ? "Training geschlossen. Anwesenheit ist jetzt schreibgeschützt."
            : "Training wieder geöffnet.",
        severity: "success",
      });
    } catch (error) {
      setFeedback({
        open: true,
        message: error?.data?.message || "Trainingsstatus konnte nicht aktualisiert werden.",
        severity: "error",
      });
    }
  };

  const handleDeleteTraining = async () => {
    if (!trainingId) return;
    try {
      await deleteTraining(trainingId).unwrap();
      navigate("/dashboard/members/attendance");
    } catch (error) {
      setFeedback({
        open: true,
        message: error?.data?.message || "Anwesenheitsliste konnte nicht gelöscht werden.",
        severity: "error",
      });
    } finally {
      setDeleteOpen(false);
    }
  };

  const handleSearchEnter = (event) => {
    if (event.key !== "Enter") return;
    if (!searchedMembers.length || isReadOnly) return;
    event.preventDefault();
    handleAddMember(searchedMembers[0]);
  };

  if (!trainingId) {
    return (
      <Container maxWidth="sm">
        <Alert severity="error">Training-ID fehlt.</Alert>
      </Container>
    );
  }

  if (trainingLoading) {
    return (
      <Container maxWidth="sm" sx={{ pb: 12, pt: 1, px: { xs: 1, sm: 2 } }}>
        <Stack spacing={1.5}>
          <Skeleton variant="rounded" height={84} />
          <Skeleton variant="rounded" height={64} />
          <Skeleton variant="rounded" height={220} />
          <Skeleton variant="rounded" height={220} />
        </Stack>
      </Container>
    );
  }

  if (trainingError || !training) {
    return (
      <Container maxWidth="sm" sx={{ pt: 1 }}>
        <Stack spacing={1.5}>
          <Alert severity="error">
            Training konnte nicht geladen werden. Bitte erneut versuchen.
          </Alert>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              onClick={() => navigate("/dashboard/members/attendance")}
            >
              Zurück
            </Button>
            <Button variant="contained" onClick={refetchTraining}>
              Erneut versuchen
            </Button>
          </Stack>
        </Stack>
      </Container>
    );
  }

  return (
    <Container
      maxWidth="sm"
      sx={{
        px: { xs: 1, sm: 2 },
        pt: { xs: 1, sm: 1.5 },
        pb: 12,
      }}
    >
      <Stack spacing={1.5}>
        <Paper
          elevation={0}
          sx={{
            position: "sticky",
            top: 8,
            zIndex: 15,
            borderRadius: 3,
            border: `1px solid ${alpha("#000", 0.1)}`,
            background: `linear-gradient(140deg, ${alpha(
              "#3f51b5",
              0.1,
            )} 0%, ${alpha("#00acc1", 0.11)} 100%)`,
          }}
        >
          <Stack spacing={1} sx={{ p: 1.5 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Button
                variant="text"
                startIcon={<Iconify icon="mdi:arrow-left" width={18} />}
                onClick={() => navigate("/dashboard/members/attendance")}
                sx={{ minHeight: 38 }}
              >
                Zurück
              </Button>
              <Stack direction="row" spacing={1} alignItems="center">
                {(isAdmin || isSuperAdmin) && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    onClick={() => setDeleteOpen(true)}
                    disabled={deletingTraining}
                    sx={{ minHeight: 36 }}
                  >
                    Liste löschen
                  </Button>
                )}
                <Button
                  size="small"
                  variant={training?.status === "closed" ? "outlined" : "contained"}
                  color={training?.status === "closed" ? "primary" : "secondary"}
                  onClick={handleCloseTraining}
                  disabled={
                    updatingTraining ||
                    (training?.status === "closed" && !canReopenClosedTraining)
                  }
                  sx={{ minHeight: 36 }}
                >
                  {training?.status === "closed" ? "Wieder öffnen" : "Schließen"}
                </Button>
              </Stack>
            </Stack>

            <Stack spacing={0.5}>
              <Typography variant="h5">Anwesenheit</Typography>
              <Typography variant="body2" color="text.secondary">
                {training?.site?.siteName || "Standort"} ·{" "}
                {toLocalDateLabel(training?.startAt)} ·{" "}
                {toLocalTimeLabel(training?.startAt)}
              </Typography>
            </Stack>

            <Chip
              label={training?.status === "closed" ? "Geschlossen" : "Offen"}
              size="small"
              color={training?.status === "closed" ? "default" : "success"}
              sx={{ width: "fit-content" }}
            />
          </Stack>
        </Paper>

        {isReadOnly && (
          <Alert severity="info">
            Dieses Training ist geschlossen. Anwesenheit ist für deine Rolle schreibgeschützt.
          </Alert>
        )}

        <Card variant="outlined" sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: 2 }}>
            <Stack spacing={1.25}>
              <TextField
                inputRef={searchInputRef}
                label="Mitglied hinzufügen"
                placeholder="Suche nach Name oder Mitgliedsnummer"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                onKeyDown={handleSearchEnter}
                fullWidth
                disabled={isReadOnly}
                InputProps={{
                  startAdornment: (
                    <Iconify
                      icon="mdi:magnify"
                      width={22}
                      sx={{ mr: 1, color: "text.secondary" }}
                    />
                  ),
                }}
              />

              {searching && (
                <Stack direction="row" spacing={1} alignItems="center">
                  <CircularProgress size={16} />
                  <Typography variant="caption" color="text.secondary">
                    Mitglieder werden gesucht...
                  </Typography>
                </Stack>
              )}

              {debouncedSearch.length >= 2 && searchedMembers.length > 0 && (
                <Stack spacing={1}>
                  {searchedMembers.map((member) => (
                    <Paper
                      key={toId(member)}
                      variant="outlined"
                      sx={{
                        borderRadius: 2,
                        px: 1.25,
                        py: 1,
                        transition: "transform 0.15s ease",
                        "&:active": { transform: "scale(0.995)" },
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                            {formatMemberName(member)}
                          </Typography>
                          <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                            {member?.memberNumber && (
                              <Chip
                                size="small"
                                label={`#${member.memberNumber}`}
                                variant="outlined"
                              />
                            )}
                            {formatDegreeLabel(member?.degree) && (
                              <Chip size="small" label={formatDegreeLabel(member?.degree)} />
                            )}
                            {member?.standortRef?.siteName && (
                              <Chip
                                size="small"
                                label={member.standortRef.siteName}
                                color="info"
                                variant="outlined"
                              />
                            )}
                          </Stack>
                        </Box>
                        <Button
                          variant="contained"
                          color="secondary"
                          onClick={() => handleAddMember(member)}
                          disabled={addingAttendee || isReadOnly}
                          sx={{ minHeight: 44, minWidth: 96 }}
                          startIcon={<Iconify icon="mdi:plus" width={18} />}
                        >
                          Hinzufügen
                        </Button>
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              )}

              {debouncedSearch.length >= 2 && !searching && searchedMembers.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  Kein passendes aktives Mitglied gefunden.
                </Typography>
              )}
            </Stack>
          </CardContent>
        </Card>

        {!debouncedSearch && recentCandidates.length > 0 && (
          <Card variant="outlined" sx={{ borderRadius: 3 }}>
            <CardContent sx={{ p: 2 }}>
              <Stack spacing={1}>
                <Typography variant="subtitle2" color="text.secondary">
                  Letzte Teilnehmer
                </Typography>
                <Stack spacing={1}>
                  {recentCandidates.map((member) => (
                    <Paper
                      key={`recent-${toId(member)}`}
                      variant="outlined"
                      sx={{ px: 1.25, py: 1, borderRadius: 2 }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                            {formatMemberName(member)}
                          </Typography>
                          <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                            {member?.memberNumber && (
                              <Chip
                                size="small"
                                label={`#${member.memberNumber}`}
                                variant="outlined"
                              />
                            )}
                            {member?.standortRef?.siteName && (
                              <Chip
                                size="small"
                                label={member.standortRef.siteName}
                                variant="outlined"
                              />
                            )}
                          </Stack>
                        </Box>
                        <Button
                          variant="outlined"
                          onClick={() => handleAddMember(member)}
                          disabled={addingAttendee || isReadOnly}
                          sx={{ minHeight: 44, minWidth: 86 }}
                          startIcon={<Iconify icon="mdi:plus" width={18} />}
                        >
                          Hinzufügen
                        </Button>
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        )}

        <Card variant="outlined" sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: 0 }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ px: 2, py: 1.5 }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Teilnehmer
              </Typography>
              <Chip size="small" label={`${attendeeCount} gesamt`} />
            </Stack>

            {sortedAttendees.length === 0 ? (
              <Stack sx={{ px: 2, pb: 2 }} spacing={1} alignItems="center">
                <Iconify
                  icon="mdi:account-group-outline"
                  width={36}
                  sx={{ color: "text.disabled" }}
                />
                <Typography variant="body2" color="text.secondary">
                  Füge oben Mitglieder hinzu, um die Anwesenheit zu starten.
                </Typography>
              </Stack>
            ) : (
              <List disablePadding>
                {sortedAttendees.map((attendee) => {
                  const member = attendee?.member || {};
                  const memberName = formatMemberName(member);
                  const memberId = attendee?.memberId;
                  const originSiteName =
                    attendee?.originSite?.siteName || member?.standortRef?.siteName || "";

                  return (
                    <ListItem
                      key={`attendee-${memberId}`}
                      divider
                      sx={{ px: 1.5, py: 1.25, alignItems: "flex-start" }}
                      secondaryAction={
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Button
                            variant={attendee?.present ? "contained" : "outlined"}
                            color={attendee?.present ? "success" : "inherit"}
                            onClick={() => handleTogglePresent(attendee)}
                            disabled={patchingAttendee || isReadOnly}
                            sx={{ minHeight: 44, minWidth: 106 }}
                            startIcon={
                              <Iconify
                                icon={
                                  attendee?.present
                                    ? "mdi:check-circle"
                                    : "mdi:checkbox-blank-circle-outline"
                                }
                                width={18}
                              />
                            }
                          >
                            {PRESENT_LABEL[String(Boolean(attendee?.present))]}
                          </Button>
                          <IconButton
                            disabled={removingAttendee || isReadOnly}
                            onClick={(event) => {
                              setMenuAnchor(event.currentTarget);
                              setMenuMemberId(String(memberId));
                            }}
                            sx={{ minWidth: 44, minHeight: 44 }}
                          >
                            <Iconify icon="mdi:dots-vertical" width={20} />
                          </IconButton>
                        </Stack>
                      }
                    >
                      <ListItemAvatar>
                        <Avatar>{getInitials(memberName)}</Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                              {memberName}
                            </Typography>
                            {member?.memberNumber && (
                              <Chip
                                size="small"
                                label={`#${member.memberNumber}`}
                                variant="outlined"
                              />
                            )}
                            {formatDegreeLabel(member?.degree) && (
                              <Chip size="small" label={formatDegreeLabel(member?.degree)} />
                            )}
                          </Stack>
                        }
                        secondary={
                          originSiteName ? (
                            <Chip
                              size="small"
                              label={originSiteName}
                              color="info"
                              variant="outlined"
                              sx={{ mt: 0.75 }}
                            />
                          ) : null
                        }
                        sx={{ pr: 20 }}
                      />
                    </ListItem>
                  );
                })}
              </List>
            )}
          </CardContent>
        </Card>

        <Paper
          elevation={4}
          sx={{
            position: "sticky",
            bottom: 8,
            borderRadius: 3,
            p: 1.25,
            border: `1px solid ${alpha("#000", 0.08)}`,
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack spacing={0.1}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Anwesend: {presentCount}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Automatisches Speichern mit optimistischen Updates
              </Typography>
            </Stack>
            <Chip
              color="primary"
              icon={<Iconify icon="mdi:content-save-check-outline" width={16} />}
              label={isReadOnly ? "Nur lesen" : "Gespeichert"}
              variant="outlined"
            />
          </Stack>
        </Paper>
      </Stack>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => {
          setMenuAnchor(null);
          setMenuMemberId("");
        }}
      >
        <MenuItem onClick={() => handleRemoveAttendee(menuMemberId)}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Iconify icon="mdi:delete-outline" width={18} />
            <Typography variant="body2">Teilnehmer entfernen</Typography>
          </Stack>
        </MenuItem>
      </Menu>

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>Anwesenheitsliste löschen</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Diese Liste wird dauerhaft gelöscht. Diese Aktion kann nicht rückgängig
            gemacht werden.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>Abbrechen</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteTraining}
            disabled={deletingTraining}
          >
            {deletingTraining ? "Löschen..." : "Löschen"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={feedback.open}
        autoHideDuration={2200}
        onClose={() => setFeedback(INITIAL_FEEDBACK)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={feedback.severity}
          variant="filled"
          onClose={() => setFeedback(INITIAL_FEEDBACK)}
        >
          {feedback.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
